import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { aiCharacterService, AICharacter } from '../../services/aiCharacterService';
import PageHeader from '../common/PageHeader';
import AICharacterCard from './AICharacterCard';
import './AICharacterManagement.css';

interface AICharacterManagementProps {
  onToggleSidebar: () => void;
}

const AICharacterManagement: React.FC<AICharacterManagementProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [characters, setCharacters] = useState<AICharacter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user) {
      fetchCharacters();
    }
  }, [user, searchTerm]);

  const fetchCharacters = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      let response;
      if (searchTerm) {
        response = await aiCharacterService.searchCharacters({
          user_id: user.id.toString(),
          search_term: searchTerm
        });
      } else {
        response = await aiCharacterService.getUserCharacters({
          user_id: user.id.toString()
        });
      }
      if (response.success && response.characters) {
        setCharacters(response.characters);
      } else {
        setError('Failed to fetch AI characters.');
      }
    } catch (err) {
      setError('An error occurred while fetching AI characters.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (characterId: number) => {
    if (!window.confirm('Are you sure you want to delete this character?')) {
      return;
    }
    try {
      const response = await aiCharacterService.deleteCharacter(characterId.toString());
      if (response.success) {
        // Refresh the list
        fetchCharacters();
      } else {
        alert('Failed to delete character.');
      }
    } catch (err) {
      alert('An error occurred while deleting the character.');
      console.error(err);
    }
  };

  const rightActions = (
    <button onClick={() => navigate('/ai-characters/new')} className="ai-character-add-btn">
      + Add Character
    </button>
  );

  return (
    <div className="ai-character-management">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="page-header-title">/ai-characters</h1>}
        rightActions={rightActions}
      />
      <div className="ai-character-management-content">
        <div className="ai-character-search-container">
          <input
            type="text"
            placeholder="Search characters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="ai-character-search-input"
          />
        </div>

        {loading && <p className="ai-character-loading">Loading...</p>}
        {error && <p className="ai-character-error">{error}</p>}
        
        {!loading && !error && (
          <div className="ai-character-list">
            {characters.length === 0 ? (
              <p className="ai-character-empty">No AI characters found. Create your first character!</p>
            ) : (
              characters.map((character) => (
                <AICharacterCard
                  key={character.id}
                  character={character}
                  onClick={() => navigate(`/ai-characters/${character.id}`)}
                  onDelete={() => handleDelete(character.id)}
                />
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AICharacterManagement;

